import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";

export interface ParcelBoxDimensions {
  depth: number;
  width: number;
  height: number;
  unit: string;
}

export interface ParcelBoxItem {
  _id: string;
  name: string;
  slug?: string;
  dimensions: ParcelBoxDimensions;
  dimensions_text?: string;
  default_price: number;
  position?: number;
  order?: number;
  is_active?: number;
  created_by?: any;
  updated_by?: any;
  created_at?: string;
  updated_at?: string;
}

export interface ParcelBoxFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
}

interface GetParcelBoxesParams {
  filters?: ParcelBoxFilterParams;
  signal?: AbortSignal;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getParcelBoxesAPI =
  ({ filters, onSuccess, onError, signal }: GetParcelBoxesParams) =>
  async (dispatch: any) => {
    try {
      const activeFilters: any = { ...filters };
      if (
        activeFilters.search !== undefined &&
        activeFilters.search.trim().length === 0
      ) {
        delete activeFilters.search;
      }

      const response = await axiosInstance.get("/parcel-boxes", {
        params: activeFilters,
        signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result?.data);
      } else {
        message.error(result?.message || "Failed to fetch parcel boxes");
        onError?.();
      }
    } catch (error: any) {
      if (
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED" ||
        error?.name === "AbortError"
      ) {
        return;
      }
      console.error("Get Parcel Boxes error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to fetch parcel boxes";
      message.error(errorMessage);
    }
  };

export interface CreateParcelBoxPayload {
  name: string;
  dimensions: ParcelBoxDimensions;
  dimensions_text?: string;
  default_price: number;
  order?: number;
  position?: number;
  is_active?: number;
}

interface CreateParcelBoxParams {
  payload: CreateParcelBoxPayload;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const createParcelBoxAPI =
  ({ payload, onSuccess, onError }: CreateParcelBoxParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post("/parcel-boxes", payload);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Parcel box created successfully");
        onSuccess?.(result?.data);
      } else {
        message.error(result?.message || "Failed to create parcel box");
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Parcel Box error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to create parcel box";
      message.error(errorMessage);
    }
  };

export interface UpdateParcelBoxPayload {
  id?: string;
  _id?: string;
  name: string;
  dimensions: ParcelBoxDimensions;
  dimensions_text?: string;
  default_price: number;
  order?: number;
  position?: number;
  is_active?: number;
}

interface UpdateParcelBoxParams {
  payload: UpdateParcelBoxPayload;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const updateParcelBoxAPI =
  ({ payload, onSuccess, onError }: UpdateParcelBoxParams) =>
  async (dispatch: any) => {
    try {
      const id = payload.id || payload._id;
      // Try PUT with payload (including id) or PUT to /parcel-boxes/:id if needed
      const response = await axiosInstance.put("/parcel-boxes", {
        ...payload,
        id,
      });
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Parcel box updated successfully");
        onSuccess?.(result?.data);
      } else {
        message.error(result?.message || "Failed to update parcel box");
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Parcel Box error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to update parcel box";
      message.error(errorMessage);
    }
  };

interface DeleteParcelBoxParams {
  id: string;
  onSuccess?: () => void;
  onError?: () => void;
}

export const deleteParcelBoxAPI =
  ({ id, onSuccess, onError }: DeleteParcelBoxParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete("/parcel-boxes", {
        data: { id },
      });
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Parcel box deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete parcel box");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Parcel Box error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to delete parcel box";
      message.error(errorMessage);
    }
  };

interface ReorderParcelBoxParams {
  payload: { id: string; order: number };
  onSuccess?: (data?: any) => void;
  onError?: () => void;
}

export const reorderParcelBoxAPI =
  ({ payload, onSuccess, onError }: ReorderParcelBoxParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put("/parcel-boxes/reorder", payload);
      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Parcel box reordered successfully");
        onSuccess?.(result?.data);
      } else {
        message.error(result?.message || "Failed to reorder parcel box");
        onError?.();
      }
    } catch (error: any) {
      console.error("Reorder Parcel Box error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "Unable to reorder parcel box";
      message.error(errorMessage);
    }
  };

